import type { CSSProperties, ReactNode } from 'react';

export interface SpinnerProps {
  /** Diameter in px. The ring border scales proportionally. Default 28 (canonical). */
  size?: number;
  label?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Canonical Onlymind loading spinner — the single ring loader shown across the
 * whole console. Every loading state should use this component (or the
 * `.om-spin` CSS class it renders) so the animation looks identical everywhere.
 */
export function Spinner({ size = 28, label = 'Loading', className, style }: SpinnerProps) {
  return (
    <span
      className={`om-spin${className ? ` ${className}` : ''}`}
      role="status"
      aria-label={label}
      style={{
        '--om-spin-size': `${size}px`,
        // Keep the ring thickness proportional to the diameter so a 16px inline
        // spinner and a 40px page spinner read as the exact same animation.
        '--om-spin-border': `${Math.max(2, size / 11)}px`,
        ...style,
      } as CSSProperties}
    />
  );
}

/**
 * Centered spinner — the canonical block for a panel/section "Loading…" state.
 * Shows only the animation (no visible text); `label` becomes the spinner's
 * accessible name for screen readers. `pad` / `size` tune it per context.
 */
export function LoadingBlock({
  label,
  size = 22,
  pad = '48px 0',
  style,
}: {
  label?: string;
  size?: number;
  pad?: string;
  /** Accepted for call-site compatibility; no visible text is rendered. */
  color?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: pad,
        ...style,
      }}
    >
      <Spinner size={size} label={label} />
    </div>
  );
}

/**
 * A button label in its busy state: the label keeps its place (so the button
 * never changes width) but turns invisible, and the ring sits over it in the
 * button's text colour. `label` ("Saving…") is what screen readers hear.
 */
export function BusyLabel({ busy, label, children }: { busy: unknown; label?: string; children: ReactNode }) {
  if (!busy) return <>{children}</>;
  return (
    <span className="om-busy" role="status" aria-label={label}>
      {children}
    </span>
  );
}

export default Spinner;
